/**
 * OnboardingTour.tsx — React Joyride v3 first-run product tour.
 * Auto-starts for new users; skip/finish persists; parent can force replay.
 */
import * as React from "react";
import { Joyride, EVENTS, STATUS, type EventData, type Step } from "react-joyride";
import { useI18n } from "@/i18n";
import {
  loadOnboarding,
  markPhaseDone,
  markTourSkipped,
  markTourCompleted,
  shouldAutoStartDataTour,
  shouldAutoStartPreviewTour,
  type TourPhase,
} from "@/utils/onboardingStore";
import { buildTourSteps, filterExistingSteps } from "./onboardingSteps";

export interface OnboardingTourProps {
  /** Current wizard step. */
  wizardStep: "data" | "preview";
  /**
   * Increment to force-start a tour (replay).
   * Pass phase via `forcePhase` (default = current wizard step).
   */
  replayNonce?: number;
  forcePhase?: TourPhase | "all";
  /** Pause while a modal/drawer steals focus (optional). */
  paused?: boolean;
}

export function OnboardingTour({
  wizardStep,
  replayNonce = 0,
  forcePhase,
  paused = false,
}: OnboardingTourProps) {
  const { t } = useI18n();
  const [run, setRun] = React.useState(false);
  const [phase, setPhase] = React.useState<TourPhase | null>(null);
  const [steps, setSteps] = React.useState<Step[]>([]);
  const startedRef = React.useRef(false);

  const locale = React.useMemo(
    () => ({
      back: t("tour.back"),
      close: t("tour.close"),
      last: t("tour.finish"),
      next: t("tour.next"),
      nextWithProgress: `${t("tour.next")} ({current}/{total})`,
      skip: t("tour.skip"),
      open: t("tour.next"),
    }),
    [t]
  );

  const startPhase = React.useCallback(
    (p: TourPhase) => {
      const built = filterExistingSteps(buildTourSteps(p, t));
      if (built.length === 0) {
        markPhaseDone(p);
        return;
      }
      setPhase(p);
      setSteps(built);
      setRun(true);
      startedRef.current = true;
    },
    [t]
  );

  // First-run auto-start (data phase).
  React.useEffect(() => {
    if (paused) return;
    if (wizardStep !== "data") return;
    if (startedRef.current && run) return;
    if (!shouldAutoStartDataTour(loadOnboarding())) return;
    const timer = window.setTimeout(() => startPhase("data"), 500);
    return () => window.clearTimeout(timer);
  }, [wizardStep, paused, startPhase, run]);

  // Preview phase once after user reaches preview (if data done, preview not).
  React.useEffect(() => {
    if (paused) return;
    if (wizardStep !== "preview") return;
    if (!shouldAutoStartPreviewTour(loadOnboarding())) return;
    const timer = window.setTimeout(() => startPhase("preview"), 700);
    return () => window.clearTimeout(timer);
  }, [wizardStep, paused, startPhase]);

  // External replay
  React.useEffect(() => {
    if (!replayNonce) return;
    const p: TourPhase =
      forcePhase === "preview"
        ? "preview"
        : forcePhase === "data"
          ? "data"
          : wizardStep === "preview"
            ? "preview"
            : "data";
    // Allow replay even if completed
    startPhase(p);
  }, [replayNonce, forcePhase, wizardStep, startPhase]);

  // Pause/resume when overlays open
  React.useEffect(() => {
    if (paused && run) setRun(false);
  }, [paused, run]);

  const handleEvent = React.useCallback(
    (data: EventData) => {
      const { status, type, action } = data;

      // Skip ends the whole onboarding (both phases).
      if (status === STATUS.SKIPPED || action === "skip") {
        setRun(false);
        markTourSkipped();
        setPhase(null);
        return;
      }

      if (type === EVENTS.TOUR_END || status === STATUS.FINISHED) {
        setRun(false);
        // If already skipped/completed, don't overwrite.
        const cur = loadOnboarding();
        if (cur.completed) {
          setPhase(null);
          return;
        }
        if (phase) {
          const next = markPhaseDone(phase);
          if (next.phasesDone.data && next.phasesDone.preview) {
            markTourCompleted();
          }
        }
        setPhase(null);
      }
    },
    [phase]
  );

  if (!run || steps.length === 0 || !phase) return null;

  return (
    <Joyride
      steps={steps}
      run={!paused}
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      locale={locale}
      options={{
        primaryColor: "hsl(215 50% 23%)",
        textColor: "#0f172a",
        backgroundColor: "#ffffff",
        overlayColor: "rgba(15, 23, 42, 0.55)",
        zIndex: 10000,
        showProgress: true,
        blockTargetInteraction: true,
        skipBeacon: true,
        spotlightRadius: 8,
        buttons: ["back", "skip", "primary"],
        closeButtonAction: "skip",
      }}
    />
  );
}
